import { useState } from 'react';

interface SupervisorModalProps {
  action: string;
  onClose: () => void;
}

export default function SupervisorModal({ action, onClose }: SupervisorModalProps) {
  const [supervisorId, setSupervisorId] = useState('');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] min-h-[300px] overflow-y-auto flex flex-col">
        <div className="bg-red-600 text-white px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div>
            <h2 className="text-lg font-bold whitespace-nowrap">Supervisor Approval Required</h2>
            <p className="text-sm text-red-100 whitespace-nowrap">Action: {action}</p>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-red-700 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <i className="ri-shield-keyhole-line text-amber-600 text-xl mt-0.5"></i>
            <div>
              <p className="text-sm font-semibold text-amber-900 mb-1 whitespace-nowrap">Authorization Required</p>
              <p className="text-sm text-amber-800 whitespace-nowrap">This action requires supervisor credentials and a valid reason code.</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 whitespace-nowrap">Supervisor ID</label>
            <input
              type="text"
              value={supervisorId}
              onChange={(e) => setSupervisorId(e.target.value)}
              placeholder="Enter supervisor ID"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 whitespace-nowrap">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 whitespace-nowrap">Reason Code (Required)</label>
            <select 
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-500"
            >
              <option value="">Select reason...</option>
              <option value="customer-request">Customer Request</option>
              <option value="pricing-error">Pricing Error</option>
              <option value="damaged-item">Damaged Item</option>
              <option value="manager-discretion">Manager Discretion</option>
              <option value="system-error">System Error</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 whitespace-nowrap">Additional Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Enter additional details..."
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-500 resize-none"
            ></textarea>
          </div>

          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
              <span className="whitespace-nowrap">Timestamp:</span>
              <span className="font-mono whitespace-nowrap">{new Date().toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between text-xs text-gray-600">
              <span className="whitespace-nowrap">Cashier:</span>
              <span className="font-semibold whitespace-nowrap">Sarah Johnson (CSH-1247)</span>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between rounded-b-xl">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer"
          >
            Cancel
          </button>
          
          <button
            disabled={!supervisorId || !password || !reason}
            className={`px-8 py-3 rounded-lg font-bold whitespace-nowrap transition-colors ${
              !supervisorId || !password || !reason
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700 text-white cursor-pointer'
            }`}
          >
            <i className="ri-shield-check-line mr-2"></i>
            Approve
          </button>
        </div>
      </div>
    </div>
  );
}
